import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../../lib/api";
import { PageHeader } from "../../components/app/Common";
import { fmtDate } from "../../lib/utils";
import { Users, MapPin, Clock, ClipboardCheck, Megaphone, ScrollText, ArrowRight } from "lucide-react";

export default function AdminConsole() {
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [onsite, setOnsite] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setSummary(data));
    api.get("/itp/metrics").then(({ data }) => setMetrics(data));
    api.get("/attendance/onsite").then(({ data }) => setOnsite(data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        title="Admin Console"
        subtitle="Company-wide overview. Metrics, on-site staff, and quick links."
      />

      {/* Big metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-border mb-10">
        <MetricCell label="Active users" value={summary?.users ?? "—"} icon={Users} />
        <MetricCell label="Pending approvals" value={summary?.pending_users ?? "—"} icon={Users} />
        <MetricCell label="Jobsites" value={summary?.jobsites ?? "—"} icon={MapPin} />
        <MetricCell label="On-site now" value={summary?.onsite ?? "—"} icon={Clock} />
        <MetricCell label="Open ITPs" value={summary?.itp_open ?? "—"} icon={ClipboardCheck} />
        <MetricCell label="Logs (24h)" value={summary?.logs_today ?? "—"} icon={ScrollText} />
        <MetricCell label="Announcements" value="LIVE" icon={Megaphone} />
        <MetricCell label="CSV Exports" value="OK" icon={ArrowRight} />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* ITP metrics visualization */}
        <div className="md:col-span-2 border border-border bg-card rounded-sm p-6">
          <div className="overline mb-2">ITP RESULTS · BY JOBSITE</div>
          <h3 className="text-lg font-semibold mb-4">Pass / Fail / N-A distribution</h3>
          {Object.keys(metrics).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-sm">
              No ITP executions yet.
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(metrics).map(([site, m]) => {
                const total = (m.pass || 0) + (m.fail || 0) + (m.na || 0) + (m.open || 0);
                return (
                  <div key={site} data-testid={`metric-site-${site}`}>
                    <div className="flex justify-between text-xs font-mono mb-1">
                      <span>{site.slice(0, 8)}…</span>
                      <span>{total} items</span>
                    </div>
                    <div className="flex h-3 rounded-sm overflow-hidden border border-border">
                      <div className="bg-[hsl(var(--status-pass))]" style={{ width: `${pct(m.pass, total)}%` }} title={`Pass: ${m.pass}`} />
                      <div className="bg-destructive" style={{ width: `${pct(m.fail, total)}%` }} title={`Fail: ${m.fail}`} />
                      <div className="bg-[hsl(var(--status-hold))]" style={{ width: `${pct(m.open, total)}%` }} title={`Open: ${m.open}`} />
                      <div className="bg-muted" style={{ width: `${pct(m.na, total)}%` }} title={`N-A: ${m.na}`} />
                    </div>
                  </div>
                );
              })}
              <div className="pt-3 flex gap-4 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                <Legend swatch="bg-[hsl(var(--status-pass))]">Pass</Legend>
                <Legend swatch="bg-destructive">Fail</Legend>
                <Legend swatch="bg-[hsl(var(--status-hold))]">Open</Legend>
                <Legend swatch="bg-muted">N-A</Legend>
              </div>
            </div>
          )}
        </div>

        {/* On site now */}
        <div className="border border-border bg-card rounded-sm p-6">
          <div className="overline mb-2">TEAM ON-SITE NOW</div>
          <h3 className="text-lg font-semibold mb-3">{onsite.length} active</h3>
          <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="onsite-list">
            {onsite.length === 0 && <div className="text-xs text-muted-foreground">Nobody clocked in.</div>}
            {onsite.map((s) => (
              <div key={s.id} className="flex items-center gap-2 border-b border-border last:border-0 py-2">
                <div className="h-7 w-7 rounded-sm bg-muted grid place-items-center font-mono text-[10px] uppercase">{(s.user_name || "?").slice(0,2)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.user_name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono truncate">{s.jobsite_name} · {fmtDate(s.clock_in)}</div>
                </div>
                <span className={`h-2 w-2 rounded-full ${s.within_geofence ? "bg-[hsl(var(--status-pass))]" : "bg-destructive"}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick nav */}
      <div className="mt-10 grid md:grid-cols-3 gap-4">
        <QuickLink to="/app/admin/users" title="Users & Access" icon={Users} />
        <QuickLink to="/app/admin/time-tracking" title="Time Tracking + CSV" icon={Clock} />
        <QuickLink to="/app/admin/itp-templates" title="ITP Templates" icon={ClipboardCheck} />
      </div>
    </div>
  );
}

function MetricCell({ label, value, icon: Icon }) {
  return (
    <div className="border-b border-r border-border p-5 -ml-px -mt-px bg-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="metric text-3xl">{value}</div>
    </div>
  );
}

function Legend({ swatch, children }) {
  return <span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-sm ${swatch}`} />{children}</span>;
}

function QuickLink({ to, title, icon: Icon }) {
  return (
    <Link to={to} className="border border-border bg-card rounded-sm p-5 hover:border-primary/60 hover:-translate-y-0.5 transition-all" data-testid={`quick-${title.toLowerCase().split(" ")[0]}`}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-sm border border-border grid place-items-center text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div className="font-medium">{title}</div>
        <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
      </div>
    </Link>
  );
}

function pct(n, t) { return t ? (n / t) * 100 : 0; }
